import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CONTACT } from "@/lib/contact";
import { WarningCircle, Trash, CheckCircle } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeleteAccount() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);
  const [formEmail, setFormEmail] = useState("");
  const [formName, setFormName] = useState("");
  const [formReason, setFormReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleImmediateDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/account/me`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t("toasts.delete_error"));
      }
      toast.success(t("toasts.delete_success"));
      setTimeout(() => {
        window.location.href = "/";
      }, 1200);
    } catch (e) {
      toast.error(e.message || t("toasts.delete_error"));
      setDeleting(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!formEmail.includes("@")) {
      toast.error(t("toasts.invalid_email"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/account/delete-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formEmail.trim(),
          full_name: formName.trim() || undefined,
          reason: formReason.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || t("toasts.generic_error"));
      setSubmitted(true);
      toast.success(t("toasts.delete_request_received"));
    } catch (e) {
      toast.error(e.message || t("toasts.generic_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="as-label text-as-green mb-3">{t("delete_account.eyebrow")}</p>
          <h1 className="as-heading text-4xl sm:text-6xl text-white leading-tight" data-testid="delete-account-title">
            {t("delete_account.title_1")} <span className="text-as-green">{t("delete_account.title_2")}</span>
          </h1>
          <p className="text-white/80 mt-6 max-w-3xl text-base leading-relaxed">
            {t("delete_account.intro")}
          </p>
        </div>
      </section>

      <article className="bg-as-surface py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <section aria-labelledby="what-deleted">
            <h2 id="what-deleted" className="as-heading text-2xl sm:text-3xl text-white mb-4 border-l-4 border-as-green pl-4">
              {t("delete_account.what_deleted_title")}
            </h2>
            <p className="text-as-gray text-sm sm:text-base mb-4">
              {t("delete_account.what_deleted_intro")}
            </p>
            <ul className="space-y-2 list-disc list-inside marker:text-as-green text-as-gray text-sm sm:text-base">
              <li>{t("delete_account.d1")}</li>
              <li>{t("delete_account.d2")}</li>
              <li>{t("delete_account.d3")}</li>
              <li>{t("delete_account.d4")}</li>
              <li>{t("delete_account.d5")}</li>
              <li>{t("delete_account.d6")}</li>
            </ul>
            <div className="mt-6 p-5 border-l-4 border-yellow-500 bg-as-black">
              <p className="text-white text-sm sm:text-base">
                {t("delete_account.fiscal_exception")}
              </p>
            </div>
          </section>

          <section aria-labelledby="path-a" className="border border-as-border bg-as-black p-8" data-testid="delete-path-logged-in">
            <div className="flex items-start gap-3 mb-2">
              <span className="as-label text-as-green">{t("delete_account.path_a_eyebrow")}</span>
            </div>
            <h2 id="path-a" className="as-heading text-2xl text-white mb-4">
              {t("delete_account.path_a_title")}
            </h2>

            {user ? (
              <>
                <p className="text-as-gray text-sm sm:text-base mb-6">
                  {t("delete_account.path_a_logged_in_intro", { email: user.email })}
                </p>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-6 as-label"
                      disabled={deleting}
                      data-testid="delete-account-btn"
                    >
                      <Trash size={20} weight="bold" className="mr-2" />
                      {deleting ? t("delete_account.path_a_deleting") : t("delete_account.path_a_button")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-as-black border-red-600 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white flex items-center gap-2">
                        <WarningCircle size={24} weight="fill" color="#ef4444" />
                        {t("delete_account.confirm_title")}
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-as-gray whitespace-pre-line">
                        {t("delete_account.confirm_body", { email: user.email })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel
                        className="bg-transparent border border-as-border text-white hover:bg-as-surface"
                        data-testid="delete-cancel-btn"
                      >
                        {t("common.cancel")}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleImmediateDelete}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold"
                        data-testid="delete-confirm-btn"
                      >
                        {t("delete_account.confirm_yes")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            ) : (
              <>
                <p className="text-as-gray text-sm sm:text-base mb-6">
                  {t("delete_account.path_a_logged_out")}
                </p>
                <a href="/login">
                  <Button
                    className="bg-as-green hover:bg-as-green/90 text-as-black font-bold px-8 py-6 as-label"
                    data-testid="delete-login-btn"
                  >
                    {t("delete_account.path_a_login_cta")}
                  </Button>
                </a>
              </>
            )}
          </section>

          <section aria-labelledby="path-b" className="border border-as-border bg-as-black p-8" data-testid="delete-path-form">
            <div className="flex items-start gap-3 mb-2">
              <span className="as-label text-as-green">{t("delete_account.path_b_eyebrow")}</span>
            </div>
            <h2 id="path-b" className="as-heading text-2xl text-white mb-4">
              {t("delete_account.path_b_title")}
            </h2>

            {submitted ? (
              <div className="p-6 border-l-4 border-as-green bg-as-surface flex items-start gap-3" data-testid="delete-form-success">
                <CheckCircle size={28} weight="fill" color="#39FF14" className="shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold mb-1">{t("delete_account.form_success_title")}</p>
                  <p className="text-as-gray text-sm">
                    {t("delete_account.form_success_body")}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleRequestSubmit} className="space-y-5">
                <p className="text-as-gray text-sm sm:text-base">
                  {t("delete_account.path_b_intro")}
                </p>

                <div>
                  <Label htmlFor="del-email" className="text-white mb-2 block">
                    {t("delete_account.form_email_label")} <span className="text-as-green">*</span>
                  </Label>
                  <Input
                    id="del-email"
                    type="email"
                    required
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    className="bg-as-surface border-as-border text-white"
                    placeholder={t("delete_account.form_email_placeholder")}
                    data-testid="delete-form-email"
                  />
                </div>

                <div>
                  <Label htmlFor="del-name" className="text-white mb-2 block">
                    {t("delete_account.form_name_label")}
                  </Label>
                  <Input
                    id="del-name"
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    className="bg-as-surface border-as-border text-white"
                    placeholder={t("delete_account.form_name_placeholder")}
                    data-testid="delete-form-name"
                  />
                </div>

                <div>
                  <Label htmlFor="del-reason" className="text-white mb-2 block">
                    {t("delete_account.form_reason_label")}
                  </Label>
                  <Textarea
                    id="del-reason"
                    value={formReason}
                    onChange={(e) => setFormReason(e.target.value)}
                    className="bg-as-surface border-as-border text-white"
                    rows={4}
                    placeholder={t("delete_account.form_reason_placeholder")}
                    maxLength={500}
                    data-testid="delete-form-reason"
                  />
                  <p className="text-xs text-as-gray-dim mt-1">
                    {t("delete_account.form_reason_max")}
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-6 as-label"
                  data-testid="delete-form-submit"
                >
                  <Trash size={18} weight="bold" className="mr-2" />
                  {submitting ? t("delete_account.form_submitting") : t("delete_account.form_submit")}
                </Button>
              </form>
            )}
          </section>

          <section aria-labelledby="direct-contact">
            <h2 id="direct-contact" className="as-heading text-2xl sm:text-3xl text-white mb-4 border-l-4 border-as-green pl-4">
              {t("delete_account.direct_contact_title")}
            </h2>
            <p className="text-as-gray text-sm sm:text-base mb-4">
              {t("delete_account.direct_contact_intro")}
            </p>
            <ul className="space-y-2 text-as-gray text-sm sm:text-base">
              <li>
                <b className="text-white">{t("delete_account.direct_email_label")}</b>{" "}
                <a
                  href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(t("delete_account.title_1") + " " + t("delete_account.title_2"))}`}
                  className="text-as-green"
                  data-testid="delete-direct-email"
                >
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <b className="text-white">{t("delete_account.direct_wa_label")}</b>{" "}
                <a
                  href={`https://wa.me/${CONTACT.phoneIntl}?text=${encodeURIComponent(t("delete_account.wa_message"))}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-as-green"
                  data-testid="delete-direct-whatsapp"
                >
                  {CONTACT.phone}
                </a>
              </li>
            </ul>
            <p className="text-xs text-as-gray-dim mt-4">
              {t("delete_account.responsible")}
            </p>
          </section>
        </div>
      </article>
    </Layout>
  );
}
